import { useCallback, useState, type FC } from "react";
import { TimePicker, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { CalendarDays, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DashboardAntDatePicker } from "src/components/reusable/AntdDatePicker/DashboardAntDatePicker";
import CampaignFormField from "src/layouts/Engagements/Campaigns/shared/CampaignFormField";
import CampaignStepCard from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import type { ScheduleSelection } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseCampaignDelivery";

const SCHEDULE_KEY = "campaignFlow.aiWizard.schedule";
const DATE_FIELD_ID = "chat-schedule-campaign-date";
const TIME_FIELD_ID = "chat-schedule-campaign-time";

const isPastDate = (current: Dayjs) => current.isBefore(dayjs().startOf("day"));

interface ChatScheduleStepProps {
  isSubmitting: boolean;
  onCancel: () => void;
  onSchedule: (_selection: ScheduleSelection) => void;
}

const ChatScheduleStep: FC<ChatScheduleStepProps> = ({ isSubmitting, onCancel, onSchedule }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);

  const handleConfirm = useCallback(() => {
    if (!date || !time) {
      message.error(t(`${SCHEDULE_KEY}.selectDateTime`));
      return;
    }

    const scheduledAt = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    if (!scheduledAt.isAfter(dayjs())) {
      message.error(t(`${SCHEDULE_KEY}.futureTime`));
      return;
    }

    onSchedule({
      sendAt: scheduledAt.toISOString(),
      date: scheduledAt.format("YYYY-MM-DD"),
      time: scheduledAt.format("HH:mm"),
    });
  }, [date, onSchedule, t, time]);

  return (
    <CampaignStepCard title={t(`${SCHEDULE_KEY}.title`)} subtitle={t(`${SCHEDULE_KEY}.subtitle`)}>
      <CampaignFormField label={t(`${SCHEDULE_KEY}.selectDate`)} htmlFor={DATE_FIELD_ID}>
        <DashboardAntDatePicker
          id={DATE_FIELD_ID}
          value={date}
          onChange={setDate}
          format="DD/MM/YYYY"
          placeholder={t(`${SCHEDULE_KEY}.datePlaceholder`)}
          disabledDate={isPastDate}
          suffixIcon={<CalendarDays size={14} aria-hidden />}
          popupClassName="campaign-flow__popup"
          className="schedule-campaign-modal__picker"
        />
      </CampaignFormField>

      <CampaignFormField label={t(`${SCHEDULE_KEY}.selectTime`)} htmlFor={TIME_FIELD_ID}>
        <TimePicker
          id={TIME_FIELD_ID}
          value={time}
          onChange={setTime}
          format="hh:mm A"
          use12Hours
          needConfirm={false}
          placeholder={t(`${SCHEDULE_KEY}.timePlaceholder`)}
          suffixIcon={<Clock size={14} aria-hidden />}
          popupClassName="campaign-flow__popup"
          className="schedule-campaign-modal__picker"
        />
      </CampaignFormField>

      <div className="campaign-flow__actions">
        <button
          type="button"
          className="button button--outline campaign-flow__action"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t(`${SCHEDULE_KEY}.cancel`)}
        </button>
        <div className="campaign-flow__actions-end">
          <button
            type="button"
            className="button button--primary campaign-flow__action"
            onClick={handleConfirm}
            disabled={!date || !time || isSubmitting}
          >
            {isSubmitting ? t(`${SCHEDULE_KEY}.scheduling`) : t(`${SCHEDULE_KEY}.confirm`)}
          </button>
        </div>
      </div>
    </CampaignStepCard>
  );
};

export default ChatScheduleStep;
